import { PERMISSIONS } from '@conversed/shared/src/central/constants/permissions';
import { WebScraperJobStatus } from '@conversed/shared/src/central/dto/web-scraper.dto';
import { z } from 'zod';
import { createRESTContract } from '../../index';
import { LLMBlockSelectionSchema } from '../data-sources/data-sources.schema';

// An exclusion may be a whole address or a leading-slash path, and a trailing
// '/*' marks it as a whole section rather than a single page.
const EXCLUSION_PATTERN = /^(https?:\/\/.+|\/.+)$/;

const WebScrapeBodySchema = z.object({
  url: z.string().url(),
  exclusions: z.string().regex(EXCLUSION_PATTERN).array().optional(),
  label: z.string().optional(),
  sharedWithCompanyIds: z.array(z.string()).optional(),
  autoTraining: z.boolean().optional(),
  autoInclude: z.boolean().optional(),
  folderId: z.string().optional(),
  expiryDate: z.string().optional(),
  selectedLLMBlocks: LLMBlockSelectionSchema.array().optional(),
  // Restricts the crawl to the URLs the website publishes in its sitemap. It
  // defaults to true on the server, so an omitted value means sitemap-only.
  sitemapOnly: z.boolean().optional(),
});

const WebScrapeResponseSchema = z.object({
  jobId: z.string(),
  status: z.enum(
    Object.values(WebScraperJobStatus) as [
      WebScraperJobStatus,
      ...WebScraperJobStatus[],
    ],
  ),
  message: z.string(),
});

const WebScraperJobStatusParamsSchema = z.object({
  jobId: z.string(),
});

const WebScraperJobStatusResponseSchema = z.object({
  jobId: z.string(),
  status: z.enum(
    Object.values(WebScraperJobStatus) as [
      WebScraperJobStatus,
      ...WebScraperJobStatus[],
    ],
  ),
  progress: z.number(),
  total: z.number(),
  truncated: z.boolean().optional(),
  sitemapOnly: z.boolean().optional(),
  url: z.string(),
  exclusions: z.array(z.string()),
  error: z.string().nullable(),
  startedAt: z.string(),
  completedAt: z.string().nullable(),
  dataSourceId: z.string().nullable(),
});

export type TWebScraperBody = z.infer<typeof WebScrapeBodySchema>;
export type TWebScraperResponse = z.infer<typeof WebScrapeResponseSchema>;
export type TWebScraperJobStatusResponse = z.infer<
  typeof WebScraperJobStatusResponseSchema
>;

export const webScraperContract = createRESTContract({
  scrapeWebsite: {
    path: '/web-scraper/scrape',
    method: 'POST',
    permissions: [PERMISSIONS.DATA_SOURCE.DATA_SOURCE_SCRAPE_WEBSITE],
    body: WebScrapeBodySchema,
    response: WebScrapeResponseSchema,
  },
  getJobStatus: {
    path: '/web-scraper/jobs/:jobId',
    method: 'GET',
    permissions: [PERMISSIONS.DATA_SOURCE.DATA_SOURCE_SCRAPE_WEBSITE],
    params: WebScraperJobStatusParamsSchema,
    response: WebScraperJobStatusResponseSchema,
  },
});
